import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  computed,
  forwardRef,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Popover, PopoverModule } from 'primeng/popover';
import moment from 'moment';
import { PrimecustomModule } from '../../primeng.custom.module';

export interface MonthRange {
  start: Date;
  end: Date;
}

@Component({
  selector: 'app-month-range-picker',
  standalone: true,
  imports: [CommonModule, PopoverModule, PrimecustomModule],
  templateUrl: './month-range-picker.component.html',
  styleUrl: './month-range-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MonthRangePickerComponent),
      multi: true,
    },
  ],
})
export class MonthRangePickerComponent implements ControlValueAccessor {
  @ViewChild('op') overlayPanel!: Popover;
  @Input() minDate?: Date;
  @Input() maxDate?: Date;
  @Input() maxRangeMonths = 3;
  @Input() placeholder = 'Select Month Range';
  @Output() rangeSelect = new EventEmitter<MonthRange>();

  readonly monthLabels = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  viewYear = signal<number>(new Date().getFullYear());
  range = signal<MonthRange | null>(null);
  hoveredMonth = signal<number | null>(null);
  private readonly pendingStart = signal<Date | null>(null);
  disabled = false;

  private onChange: (value: MonthRange | null) => void = (_value) => undefined;
  private onTouched: () => void = () => undefined;
  displayLabel = computed(() => {
    const r = this.range();
    if (!r) return '';
    const start = moment(r.start).format('MMM YYYY');
    const end = moment(r.end).format('MMM YYYY');
    return start === end ? start : `${start} - ${end}`;
  });

  canGoPrevYear = computed(
    () =>
      !this.minDate ||
      moment(this.minDate).isBefore(
        moment({ year: this.viewYear(), month: 0, day: 1 })
      )
  );
  canGoNextYear = computed(
    () =>
      !this.maxDate ||
      moment(this.maxDate).isAfter(
        moment({ year: this.viewYear(), month: 11, day: 1 }).endOf('month')
      )
  );

  writeValue(value: MonthRange | null): void {
    this.range.set(value ?? null);
    this.pendingStart.set(null);
    if (value?.start) this.viewYear.set(moment(value.start).year());
  }
  registerOnChange(fn: (value: MonthRange | null) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  toggle(event: Event): void {
    if (!this.disabled) this.overlayPanel.toggle(event);
  }
  prevYear(): void {
    if (this.canGoPrevYear()) this.viewYear.update((y) => y - 1);
  }
  nextYear(): void {
    if (this.canGoNextYear()) this.viewYear.update((y) => y + 1);
  }

  isMonthDisabled(monthIndex: number): boolean {
    const cell = moment({ year: this.viewYear(), month: monthIndex, day: 1 });
    if (this.minDate && cell.isBefore(moment(this.minDate).startOf('month')))
      return true;
    if (this.maxDate && cell.isAfter(moment(this.maxDate).endOf('month')))
      return true;
    return false;
  }

  onMonthHover(monthIndex: number): void {
    if (this.pendingStart()) this.hoveredMonth.set(monthIndex);
  }
  onMonthLeave(): void {
    this.hoveredMonth.set(null);
  }

  /**
   * Single source of truth for the max-range clamp.
   * Given a start/end pair, returns the (possibly shortened) end date
   * plus whether clamping occurred.
   */
  private clampEnd(start: Date, end: Date): { end: Date; clamped: boolean } {
    const startM = moment(start).startOf('month');
    const endM = moment(end).startOf('month');
    const spanMonths = endM.diff(startM, 'months') + 1;
    if (spanMonths > this.maxRangeMonths) {
      return {
        end: moment(startM)
          .add(this.maxRangeMonths - 1, 'months')
          .toDate(),
        clamped: true,
      };
    }
    return { end, clamped: false };
  }

  onMonthClick(monthIndex: number): void {
    if (this.disabled || this.isMonthDisabled(monthIndex)) return;
    if (this.isBeyondLimit(monthIndex)) return;

    const clicked = moment({ year: this.viewYear(), month: monthIndex, day: 1 })
      .startOf('month')
      .toDate();
    const pending = this.pendingStart();

    if (!pending) {
      this.pendingStart.set(clicked);
      const singleMonthRange: MonthRange = { start: clicked, end: clicked };
      this.range.set(singleMonthRange);

      this.onChange(singleMonthRange);
      this.onTouched();
      this.rangeSelect.emit(singleMonthRange);
      return;
    }
    if (moment(clicked).isBefore(pending, 'month')) {
      this.pendingStart.set(clicked);
      const singleMonthRange: MonthRange = { start: clicked, end: clicked };
      this.range.set(singleMonthRange);
      this.onChange(singleMonthRange);
      this.onTouched();
      this.rangeSelect.emit(singleMonthRange);
      return;
    }

    const { end } = this.clampEnd(pending, clicked);
    const finalRange: MonthRange = { start: pending, end };
    this.range.set(finalRange);
    this.pendingStart.set(null);
    this.hoveredMonth.set(null);
    this.onChange(finalRange);
    this.onTouched();
    this.rangeSelect.emit(finalRange);
    this.overlayPanel.hide();
  }

  getMonthClasses(monthIndex: number): Record<string, boolean> {
    const year = this.viewYear();
    const cell = moment({ year, month: monthIndex, day: 1 });
    const r = this.range();
    const pending = this.pendingStart();
    const hovered = this.hoveredMonth();
    let isStart = false,
      isEnd = false,
      isInRange = false,
      isPreview = false,
      isBeyondLimit = false;

    if (r) {
      const startM = moment(r.start).startOf('month');
      const endM = moment(r.end).startOf('month');
      isStart = cell.isSame(startM, 'month');
      isEnd = cell.isSame(endM, 'month');
      isInRange = cell.isAfter(startM, 'month') && cell.isBefore(endM, 'month');
    }
    if (pending && hovered !== null) {
      const pendingM = moment(pending).startOf('month');
      const hoverM = moment({ year, month: hovered, day: 1 });
      const trueLo = pendingM.isSameOrBefore(hoverM) ? pendingM : hoverM;
      const trueHi = pendingM.isSameOrBefore(hoverM) ? hoverM : pendingM;
      const { end: clampedHi, clamped } = this.clampEnd(
        trueLo.toDate(),
        trueHi.toDate()
      );
      const hi = moment(clampedHi);

      isPreview =
        cell.isSameOrAfter(trueLo, 'month') && cell.isSameOrBefore(hi, 'month');
      if (clamped) {
        isBeyondLimit =
          cell.isAfter(hi, 'month') && cell.isSameOrBefore(trueHi, 'month');
      }
    }

    return {
      'mrp-month--start': isStart,
      'mrp-month--end': isEnd,
      'mrp-month--in-range': isInRange,
      'mrp-month--preview': isPreview && !isStart && !isEnd && !isInRange,
      'mrp-month--beyond-limit': isBeyondLimit,
      'mrp-month--disabled': this.isMonthDisabled(monthIndex),
    };
  }
  private isBeyondLimit(monthIndex: number): boolean {
    const pending = this.pendingStart();
    if (!pending) return false;

    const cell = moment({ year: this.viewYear(), month: monthIndex, day: 1 });
    const pendingM = moment(pending).startOf('month');
    if (cell.isBefore(pendingM, 'month')) return false; // re-picking start always allowed

    return cell.diff(pendingM, 'months') + 1 > this.maxRangeMonths;
  }
}
